// Hooks for the leads / pipeline / jobs / contacts slice: typed views of the
// read rows, the mock-only reads (materials and notes) until the
// desks and S9/S11 land, and the one way a page mutates — a proposal.
import { useCallback, useEffect, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProposalResult } from "@artemis-group/aiw-contracts";
import { api, client, fetchImpl } from "../lib/api";
import { env } from "../lib/env";


// ── Row shapes (services/api/src/rows.ts, plus the columns the mock carries) ──
export interface Lead {
  channel_signals?: { response?: { owner_name?: string; owner_user_id: string; status: string; version: number }; cost_estimated?: boolean };
  id: string; received_at: string; source_id: string; channel: string; channel_job_id: string | null; facet: string | null;
  cost: number | null; cost_basis: string | null; raw_brief: string | null; category: string | null; suburb: string | null; postcode: string | null;
  contact_hint: { name?: string; phone?: string; email?: string } | null; contact_id: string | null; contact_name: string | null; job_ref: string | null;
  disposition: string | null; disposition_reason: string | null; disposition_at: string | null; effective_stage: string | null; stage_group: string | null;
  outcome: string | null; deal_value: number | null; created_at: string; updated_at: string; owner?: string | null; data_origin?: string;
}
export interface Job {
  ref: string; name: string | null; raw_stage: string | null; effective_stage: string | null; stage_group: string | null; outcome: string | null;
  deal_value: number | null; contact_id: string | null; account_id: string | null; suburb: string | null; creation_date: string | null; close_date: string | null;
  install_gate: string | null; install_next_date: string | null; lead_id: string | null; workflow_flags: Record<string, unknown> | null; created_at: string; updated_at: string;
  address?: string | null; product?: string | null; area_m2?: number | null; stairs?: number | null; payment_method?: string | null; deposit_pct?: number | null; cash_payments_active?:boolean;
  deposit_amount?: number | null;
  deposit_date?: string | null; date_paid?: string | null; tags?: string[] | null; notes?: string | null;
}
export interface InstallDay { id: string | null; date: string; crew: string | null; task: string | null; done: string | null; office_state?: {state?:"closed"|"open";at?:string;by?:string;notes?:string}|null; assignee?: {kind?:string;id?:string;name?:string;route?:string}|null; task_ids?: string[] }
export interface Install { id: string; job_ref: string; team: string | null; gate: string | null; next_date: string | null; schedule_col_count: number; updated_at: string; days?: InstallDay[] }
export interface Quote { t1_summary?:unknown; id: string; job_ref: string; option_name: string | null; is_primary: boolean | null; subtotal: number | null; grand_total: number | null; created_at: string }
export interface Contact {
  id: string; name: string | null; email: string | null; phone: string | null; contact_type: string | null; lifecycle: string | null; job_count: number | null;
  created_at: string; updated_at: string; suburb?: string | null; prefers?: string | null;
  /** The business this person belongs to, if any. No account is normal — most
   *  customers are retail and have no business behind them. */
  account_id?: string | null;
  address?: string | null; site_suburb?: string | null; customer_view?: string; work_groups?: string[]; active_job_count?: number; pipeline_count?: number; lead_count?: number; latest_work_ref?: string|null; latest_work_name?: string|null;
}
export interface Material { item: string; detail: string; qty: string; source: string; due: string | null; due_note: string; status: "at risk" | "confirmed" | "allocated" | "not ordered" | "on order"; status_note: string }
export interface JobNote { at: string; who: string; text: string; important: boolean }
export interface Home { id: string; address: string; measured_by: string; measured_at: string; rooms: { name: string; w: number; h: number; m2: number }[]; total_m2: number; notes: string }
export interface ContactHome { id: string; label: string; address: Record<string,string>; suburb: string|null; postcode: string|null; measurements: {id:string;job_ref:string|null;method:string;disclaimer_required:boolean;measured_at:string;summary:{address?:string|null;area_m2?:number;rooms?:{name:string;floor:string;finish:string|null;gross_m2:number|null;laid_m2:number|null;source:string;measured:Record<string,number>}[]}}[] }
export interface Message { at: string; direction: "in" | "out"; channel: string; who: string; text: string }

// ── mock-only reads (live returns [] until the tables exist) ──
const mockRead = async <T,>(path: string): Promise<T> => {
  const r = await fetchImpl(`${env.apiBase}${path}`, { headers: { "x-aiw-surface": "crm" } });
  const j = (await r.json()) as { ok: true; data: T };
  return j.data;
};
const mockOnly = <T,>(path: string) => (env.mock ? mockRead<T[]>(path) : Promise.resolve([] as T[]));

export const useJobMaterials = (ref: string) => useQuery({ queryKey: ["job", ref, "materials"], queryFn: () => mockOnly<Material>(`/api/mock/job/${ref}/materials`), enabled: !!ref });
export const useJobNotes = (ref: string) => useQuery({ queryKey: ["job", ref, "notes"], queryFn: () => mockOnly<JobNote>(`/api/mock/job/${ref}/notes`), enabled: !!ref });
export const useContactHomes = (id: string) => useQuery({ queryKey: ["contact", id, "homes"], queryFn: async () => env.mock ? [] as ContactHome[] : await api.contactRecords(client,id,"homes") as unknown as ContactHome[], enabled: !!id });
export const useContactMessages = (id: string) => useQuery({ queryKey: ["contact", id, "touches"], queryFn: async () => {
  if (env.mock) return mockOnly<Message>(`/api/mock/contact/${id}/messages`);
  const rows = await api.contactRecords(client,id,"touches");
  return rows.map(r=>({at:String(r.at),direction:r.direction as "in"|"out",channel:String(r.channel),who:"Recorded contact",text:String(r.summary ?? "")}));
}, enabled: !!id });
export const useContactLeads = (id: string) => useQuery({queryKey:["contact",id,"leads"],enabled:!!id,queryFn:async()=>env.mock ? ((await api.leads(client)) as unknown as Lead[]).filter(l=>l.contact_id===id) : await api.contactRecords(client,id,"leads") as unknown as Lead[]});

/** Fetch the exact contact; the first list page is not a customer directory. */
export function useContact(id: string) {
  const q = useQuery({queryKey:["contact",id],enabled:!!id,queryFn:async()=>env.mock ? ((await api.contacts(client)) as unknown as Contact[]).find(c=>c.id===id) ?? null : await api.contact(client,id) as unknown as Contact|null});
  return {...q,contact:q.data ?? null};
}
/** Fetch related jobs before paging, including work billed to this account. */
export function useContactJobs(contactId: string) {
  const q = useQuery({queryKey:["contact",contactId,"jobs"],enabled:!!contactId,queryFn:async()=>env.mock ? ((await api.jobs(client)) as unknown as Job[]).filter(j=>j.contact_id===contactId||j.account_id===contactId) : await api.contactRecords(client,contactId,"jobs") as unknown as Job[]});
  return {...q,jobs:q.data ?? []};
}
/** Quotes for many jobs at once (Contact 360). */
export function useQuotesFor(refs: string[]) {
  return useQueries({
    queries: refs.map((ref) => ({ queryKey: ["job", ref, "quotes"], queryFn: () => api.jobQuotes(client, ref) as Promise<unknown[]> })),
    combine: (rs) => ({ quotes: rs.flatMap((r) => (r.data ?? []) as Quote[]), pending: rs.some((r) => r.isPending), isError: rs.some(r=>r.isError) }),
  });
}

/** A clock that ticks so SLA countdowns move without a refetch. */
export function useNow(everyMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), everyMs); return () => clearInterval(t); }, [everyMs]);
  return now;
}

// ── the one mutation path: propose, show the backbone's answer, refetch ──
export type ProposalNote = { tone: "ok" | "wait" | "bad"; text: string; at: number };

export function useProposal() {
  const qc = useQueryClient();
  const [note, setNote] = useState<ProposalNote | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 5000);
    return () => clearTimeout(t);
  }, [note]);

  /** `done` is the plain-words sentence for the toast ("Call logged"). */
  const run = useCallback(async (command: string, args: Record<string, unknown>, done: string, onError?: (message: string) => void): Promise<ProposalResult | null> => {
    setBusy(true);
    try {
      const r = await api.propose(client, { command, args, idempotency_key: crypto.randomUUID() });
      if (r.status === "executed") setNote({ tone: "ok", text: `${done} · done`, at: Date.now() });
      else if (r.status === "pending") setNote({ tone: "wait", text: "Awaiting approval — no change has been made", at: Date.now() });
      else { setNote({ tone: "bad", text: `Not done · ${r.reason}`, at: Date.now() }); onError?.(r.reason); }
      await qc.invalidateQueries();
      return r;
    } catch (e) {
      setNote({ tone: "bad", text: `Not done · ${(e as Error).message}`, at: Date.now() });
      onError?.((e as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  }, [qc]);

  return { run, note, busy, clear: () => setNote(null) };
}

export const useContactQuotes = (id:string, refs:string[]) => {
  const mock = useQuotesFor(env.mock ? refs : []);
  const live = useQuery({queryKey:["contact",id,"quotes"],enabled:!!id&&!env.mock,queryFn:async()=>await api.contactRecords(client,id,"quotes") as unknown as Quote[]});
  return env.mock ? {...mock,isPending:mock.pending} : {...live,quotes:live.data ?? []};
};

export const useLeadTouches = (id: string) => useQuery({queryKey:["lead",id,"touches"],enabled:!!id,queryFn:()=>api.leadTouches(client,id)});

export const useLeadPrice = (id:string) => useQuery({queryKey:["lead",id,"price"],enabled:!!id,queryFn:()=>api.leadPrice(client,id)});
